import { Box, Flex, HStack, Input, Link, Stack, Text } from '@chakra-ui/react';
import { useSignIn, useSignUp } from '@clerk/nextjs';
import { useRollbar } from '@rollbar/react';
import { useHandshakeRecovery } from './handshake';
import React from 'react';
import { PillButton, ResendControl, SurfaceCard } from '../../guest-ui/primitives';

const CODE_LENGTH = 6;

const COLLECTABLE = ['password', 'email_address', 'first_name', 'last_name'] as const;

const FIELD_LABEL: Record<string, string> = {
  password: 'Create a password',
  email_address: 'Email address',
  first_name: 'First name',
  last_name: 'Last name',
};

export const OtpStep = ({
  mode,
  channel,
  sentTo,
  onBack,
  onComplete,
}: {
  mode: 'signUp' | 'signIn';
  channel: 'phone' | 'email';
  sentTo?: string;
  onBack?: () => void;
  onComplete?: () => void;
}) => {
  const { signUp, setActive: setSignUpActive } = useSignUp();
  const { signIn, setActive: setSignInActive } = useSignIn();

  const [code, setCode] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [attempts, setAttempts] = React.useState(0);
  const [focused, setFocused] = React.useState(false);
  const [activeChannel, setActiveChannel] = React.useState(channel);
  // …and the destination has to move WITH it. `sentTo` is the address the parent sent the first code to; on a phone → email hand-off it goes stale, which is what made the screen read "Check your email / We sent it to (617) 555-0100." Both switch points below update this together.
  const [destination, setDestination] = React.useState(sentTo);

  const handOffToEmail = (address?: string | null) => {
    setActiveChannel('email');
    setDestination(address || (signUp as any)?.emailAddress || undefined);
  };
  const [missing, setMissing] = React.useState<string[] | null>(null);
  const [extra, setExtra] = React.useState<Record<string, string>>({});
  const inputRef = React.useRef<HTMLInputElement>(null);
  const rollbar = useRollbar();
  const handshake = useHandshakeRecovery();

  const locked = attempts >= 3;

  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const clerkError = (err: any, fallback: string) =>
    err?.errors?.[0]?.longMessage || err?.errors?.[0]?.message || fallback;

  const finish = async (res: any) => {
    if (res.status !== 'complete') return false;
    try {
      await setSignUpActive({ session: res.createdSessionId });
    } catch (err) {
      // The account exists and the session cookie is set; the client-side handshake can transiently 404 — reload and pick the session up.
      if (!handshake.recover(err, 'signUp')) {
        setError('We could not finish signing you in. Please refresh and try again.');
      }
      return true;
    }
    handshake.clear();
    onComplete?.();
    return true;
  };

  const submit = async (value: string) => {
    if (submitting || locked || missing) return;
    setError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      if (mode === 'signUp') {
        const res =
          activeChannel === 'phone'
            ? await signUp.attemptPhoneNumberVerification({ code: value })
            : await signUp.attemptEmailAddressVerification({ code: value });
        if (await finish(res)) return;
        const fields = ((res as any).missingFields ?? []) as string[];
        const collectable = fields.filter((f) => COLLECTABLE.includes(f as any));
        if (collectable.length) {
          setMissing(collectable);
          return;
        }
        if (
          activeChannel === 'phone' &&
          ((res as any).unverifiedFields ?? []).includes('email_address')
        ) {
          await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
          handOffToEmail();
          setCode('');
          setAttempts(0);
          setNotice('Your number is verified — one more code to confirm your email.');
          return;
        }
        setError(
          fields.length
            ? `Your account still needs: ${fields.join(', ')}. Please contact us and we'll finish it.`
            : 'That code was accepted but the account could not be completed. Please try another method.',
        );
      } else {
        const res = await signIn.attemptFirstFactor({
          strategy: activeChannel === 'phone' ? 'phone_code' : 'email_code',
          code: value,
        });
        if (res.status === 'complete') {
          try {
            await setSignInActive({ session: res.createdSessionId });
          } catch (err) {
            if (!handshake.recover(err, 'signIn')) {
              setError('We could not finish signing you in. Please refresh and try again.');
            }
            return;
          }
          handshake.clear();
          onComplete?.();
          return;
        }
        setError('We need one more step to finish signing you in.');
      }
    } catch (err: any) {
      const next = attempts + 1;
      setAttempts(next);
      setCode('');
      setError(clerkError(err, "That code didn't match. Check it and try again."));
      inputRef.current?.focus();
      // A mistyped code is normal and stays at info; running out of attempts is the shape a systemic failure takes (wrong strategy, expired codes, Clerk misconfig), and without this the two are indistinguishable in aggregate.
      const code = err?.errors?.[0]?.code;
      rollbar[next >= 3 ? 'error' : 'info'](
        `[otp] verification attempt failed (${mode}/${activeChannel})`,
        { mode, channel: activeChannel, attempt: next, clerkCode: code },
      );
    } finally {
      setSubmitting(false);
    }
  };

  const submitMissing = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const payload: Record<string, string> = {};
      if (missing?.includes('password')) payload.password = extra.password ?? '';
      if (missing?.includes('email_address')) payload.emailAddress = extra.email_address ?? '';
      if (missing?.includes('first_name')) payload.firstName = extra.first_name ?? '';
      if (missing?.includes('last_name')) payload.lastName = extra.last_name ?? '';
      const res = await signUp.update(payload as any);
      if (await finish(res)) return;
      // Re-supplying an SSO-claimed email just gets dropped again — don't loop.
      if (payload.emailAddress && !(res as any).emailAddress) {
        setError(
          "That email is managed by single sign-on, so we can't attach it here. Use Google or Facebook above, or a different email address for your trip.",
        );
        return;
      }
      if (((res as any).unverifiedFields ?? []).includes('email_address')) {
        await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
        handOffToEmail(payload.emailAddress);
        setMissing(null);
        setCode('');
        setAttempts(0);
        setNotice('Your number is verified — one more code to confirm your email.');
        return;
      }
      setError('We could not finish creating your account. Please try another method.');
    } catch (err: any) {
      setError(clerkError(err, 'That did not work. Please check the details and try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async () => {
    setError(null);
    setNotice(null);
    setCode('');
    try {
      if (mode === 'signUp') {
        if (activeChannel === 'phone') {
          await signUp.preparePhoneNumberVerification({ strategy: 'phone_code' });
        } else {
          await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
        }
      } else {
        const factor = signIn.supportedFirstFactors?.find(
          (f: any) => f.strategy === (activeChannel === 'phone' ? 'phone_code' : 'email_code'),
        ) as any;
        if (factor) {
          await signIn.prepareFirstFactor({
            strategy: activeChannel === 'phone' ? 'phone_code' : 'email_code',
            phoneNumberId: factor.phoneNumberId,
            emailAddressId: factor.emailAddressId,
          } as any);
        }
      }
      setAttempts(0);
      inputRef.current?.focus();
    } catch (err: any) {
      setError(clerkError(err, "We couldn't send another code just yet."));
      throw err; // lets ResendControl skip its cooldown for a send that never happened
    }
  };

  const boxes = Array.from({ length: CODE_LENGTH }, (_, i) => code[i] ?? '');
  const caretAt = focused && code.length < CODE_LENGTH ? code.length : -1;

  const errorNote = error && (
    <Box bg="red.50" border="1px solid" borderColor="red.100" borderRadius="6px" p={3}>
      <Text fontSize="13px" lineHeight="19px" fontWeight={500} color="red.600">
        {error}
      </Text>
    </Box>
  );

  const noticeNote = notice && (
    <Box bg="blue.50" border="1px solid" borderColor="blue.100" borderRadius="6px" p={3}>
      <Text fontSize="13px" lineHeight="19px" fontWeight={500} color="dmNavy.500">
        {notice}
      </Text>
    </Box>
  );

  if (missing) {
    return (
      <Stack spacing={4} w="100%">
        <Stack spacing="6px">
          <Text
            fontSize="12px"
            fontWeight={600}
            letterSpacing="0.8px"
            textTransform="uppercase"
            color="dmOrange.500"
          >
            Almost there
          </Text>
          <Text
            as="h2"
            fontSize={{ base: '23px', md: '28px' }}
            lineHeight={{ base: '28px', md: '34px' }}
            fontWeight={700}
            color="dmNavy.500"
          >
            One last detail
          </Text>
          <Text fontSize={{ base: '14px', md: '16px' }} color="gray.600">
            Your number is verified. We just need a little more to finish your account.
          </Text>
        </Stack>

        <SurfaceCard p={{ base: 4, md: '28px' }}>
          <Stack spacing={4}>
            {errorNote}
            {missing.map((field, i) => (
              <Box key={field}>
                <Text fontSize="12px" fontWeight={500} color="gray.700" mb={1}>
                  {FIELD_LABEL[field] ?? field}
                </Text>
                <Input
                  h="52px"
                  borderRadius="6px"
                  borderColor="gray.200"
                  _focusVisible={{ borderColor: 'dmNavy.500', boxShadow: 'none' }}
                  fontSize="16px"
                  autoFocus={i === 0}
                  type={
                    field === 'password' ? 'password' : field === 'email_address' ? 'email' : 'text'
                  }
                  autoComplete={field === 'password' ? 'new-password' : undefined}
                  placeholder={field === 'password' ? 'At least 8 characters' : undefined}
                  value={extra[field] ?? ''}
                  onChange={(e) => setExtra((p) => ({ ...p, [field]: e.target.value }))}
                />
              </Box>
            ))}
            <PillButton
              w="100%"
              isLoading={submitting}
              loadingText="Finishing"
              onClick={submitMissing}
            >
              Finish my account
            </PillButton>
          </Stack>
        </SurfaceCard>
      </Stack>
    );
  }

  return (
    <Stack spacing={4} w="100%">
      <Stack spacing="6px">
        <Text
          fontSize="12px"
          fontWeight={600}
          letterSpacing="0.8px"
          textTransform="uppercase"
          color="dmOrange.500"
        >
          {activeChannel === 'phone' ? 'Check your texts' : 'Check your email'}
        </Text>
        <Text
          as="h2"
          fontSize={{ base: '23px', md: '28px' }}
          lineHeight={{ base: '28px', md: '34px' }}
          fontWeight={700}
          color="dmNavy.500"
        >
          Enter your 6-digit code
        </Text>
        <Text fontSize={{ base: '14px', md: '16px' }} color="gray.600">
          {destination ? `We sent it to ${destination}.` : 'We just sent it over.'}
        </Text>
      </Stack>

      <SurfaceCard p={{ base: 4, md: '28px' }}>
        <Stack spacing={4}>
          <Box position="relative" maxW="420px">
            <Input
              ref={inputRef}
              value={code}
              onChange={(e) => {
                const next = e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
                setCode(next);
                if (next.length === CODE_LENGTH) submit(next);
              }}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              autoFocus
              inputMode="numeric"
              autoComplete="one-time-code"
              aria-label="Verification code"
              position="absolute"
              inset={0}
              w="100%"
              h="56px"
              opacity={0}
              zIndex={1}
              isDisabled={locked}
            />
            <HStack spacing={2} justify="space-between">
              {boxes.map((digit, i) => (
                <CodeBox
                  key={i}
                  filled={Boolean(digit)}
                  error={Boolean(error)}
                  active={i === caretAt}
                >
                  {digit}
                </CodeBox>
              ))}
            </HStack>
          </Box>

          {noticeNote}
          {errorNote}

          {locked ? (
            <Stack spacing={3}>
              <Text fontSize="14px" color="gray.600">
                That's several tries without a match. Wait a moment and request a new code, or call
                us and we'll sort it out.
              </Text>
              <PillButton pill="navy" as="a" href="tel:5082717603" w="100%">
                Call 508-271-7603
              </PillButton>
            </Stack>
          ) : (
            <PillButton
              w="100%"
              isLoading={submitting}
              loadingText="Checking"
              onClick={() => submit(code)}
              isDisabled={code.length !== CODE_LENGTH}
            >
              Continue
            </PillButton>
          )}

          <Stack direction="row" justify="space-between" align="center">
            <ResendControl key={activeChannel} onResend={resend} />
            {onBack && (
              <Link fontSize="13px" color="dmNavy.500" onClick={onBack}>
                Use a different method
              </Link>
            )}
          </Stack>
        </Stack>
      </SurfaceCard>
    </Stack>
  );
};

const CodeBox = ({
  children,
  filled,
  error,
  active,
}: {
  children: React.ReactNode;
  filled: boolean;
  error: boolean;
  active: boolean;
}) => (
  <Flex
    flex={1}
    h="56px"
    align="center"
    justify="center"
    borderRadius="6px"
    border={error || active ? '2px solid' : '1px solid'}
    borderColor={error ? 'red.500' : active || filled ? 'dmNavy.500' : 'gray.200'}
    fontSize="22px"
    fontWeight={600}
    color="dmNavy.500"
  >
    {children || (active && <Caret />)}
  </Flex>
);

const Caret = () => (
  <Box
    w="2px"
    h="24px"
    bg="dmNavy.500"
    sx={{
      '@keyframes dmCaretBlink': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0 } },
      animation: 'dmCaretBlink 1.06s step-end infinite',
    }}
  />
);
